import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import {
  IconBtn,
  LabelValueItem,
  UiSelect,
} from '@ru-digital-assets/ui-component';
import { get } from 'lodash';
import { Wallet } from 'ethers';
import SignIcon from '../../../assets/icons/sign-icon';
import DetailsContainer from '../../../containers/details-container';
import GoBack from '../../../containers/go-back';
import AppPageMain from '../../../modules/app-page/app-page--main';
import { LoadDocBtn } from '@ru-digital-assets/ui-component';
import PageHeader from '../../../modules/page-header';
import { useAppDispatch } from '../../../store';
import {
  getIssue,
  getPurchasesOperatorCompany,
  getUcpDoc,
  paymentConfirm,
  paymentUpload,
  signIssue,
  signOperation,
} from '../../../store/actions/operations-sign';
import {
  RootState,
  SIGN_DOC_TYPE,
} from '../../../types';
import currencyFormat from '../../../utils/currencyFormat';
import {
  downloadFile,
} from '../../../utils/files/downloadFile';
import { getIssueBadge } from '../../../utils/getStatusBadge';
import { getFormatDate } from '../../../utils/utils';
import '../../../modules/common-icons/modal.scss';
import { PrivateKeyButton } from 'components';
import './index.scss';
import { setPush } from '../../../store/actions/app';
import { BlockchainTransactionInfo } from 'components';
import { OperationComplete, BlockchainExplorerButton } from 'components';
import { CopyIcon } from 'assets/icons';
import { useSign } from 'hooks';
import AppCheckbox from '../../../modules/app-checkbox/app-checkbox';
import issueApi from 'services/api/IssueApi';

const CfaInfoPage: FC = () => {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const [isSigned, setSigned] = useState(false);
  const [signMode, setSignMode] = useState('');
  const [transactionId, setTransactionId] = useState<string>();
  const [approvePayment, setApprovePayment] = useState(false);
  const [approveRisk, setApproveRisk] = useState(false);
  const isHistory = useMemo(() => history.location.state?.isHistory, [history]);
  const id = useMemo(() => history.location.state?.id, [history]);
  const issue: any = useSelector(
    (state: RootState) => state.operationsSign.data?.issue
  );
  const token = useSelector((s: RootState) => s.auth.account.token);

  useEffect(() => {
    id && dispatch(getIssue({ id }));
    dispatch(getPurchasesOperatorCompany());
  }, [id]);

  const issueSigAvailable = [
    'ISSUER_DOC_SIGNED',
    'ISSUER_SIGNED',
    'ISSUE_ACCEPT_SIGNED',
    'COMPLETED',
    'RECIPIENT_SIGNED',
    'RECIPIENT_DOC_SIGNED',
    'PAYMENT_DOCUMENT_UPLOADED',
  ].includes(issue?.status);

  const sigAvailable = [
    'ISSUER_DOC_SIGNED',
    'ISSUE_ACCEPT_SIGNED',
    'COMPLETED',
    'RECIPIENT_SIGNED',
    'RECIPIENT_DOC_SIGNED',
    'PAYMENT_DOCUMENT_UPLOADED',
  ].includes(issue?.status);

  const isSignActive = useMemo(() => {
    if (!issue) return false;
    if (!issue.isIssuer) {
      return issue.status === 'ISSUER_SIGNED';
    } else {
      return ['INITIAL', 'PAYMENT_DOCUMENT_UPLOADED'].includes(issue.status);
    }
  }, [issue]);

  const {
    certificatesList,
    selectedCertificate,
    signEntity,
    onUserSelectCertificate,
    usersCertificates,
  } = useSign(isSignActive);

  const currentCert = useMemo(() => {
    return usersCertificates.find(
      (certificate) => certificate.thumbprint === selectedCertificate?.value
    );
  }, [usersCertificates, selectedCertificate]);

  const downloadDoc = useCallback(
    (type: SIGN_DOC_TYPE, title: string) => {
      if (issue?.decision) {
        issueApi
          .getIssueDoc(token, issue?.decision.id, type, 'application/pdf')
          .then((response) => {
            downloadFile(response.data, title, 'application/pdf');
          })
          .catch(() =>
            dispatch(
              setPush({
                message: 'Ошибка при загрузке файла',
                type: 'error',
              })
            )
          );
      }
    },
    [issue]
  );

  // const downloadAdditionalDoc = useCallback(
  //   (type: GARANT_ISSUE_DOC_TYPE, title: string) => {
  //     if (issue?.decision) {
  //       issueApi
  //         .getAdditionalIssueDoc(
  //           token,
  //           issue?.decision.id,
  //           type,
  //           type === 'GARANT_ZIP'
  //             ? 'application/octet-stream'
  //             : 'application/pdf'
  //         )
  //         .then((response) => {
  //           downloadFile(response.data, title, 'application/pdf');
  //         })
  //         .catch(() =>
  //           dispatch(
  //             setPush({
  //               message: 'Ошибка при загрузке файла',
  //               type: 'error',
  //             })
  //           )
  //         );
  //     }
  //   },
  //   [issue]
  // );

  const decisionDocs = useMemo(() => {
    const docs = [];

    if (issue?.decision)
      docs.push(
        <LoadDocBtn
          title={`Решение о выпуске №${issue?.id}`}
          extension=".PDF"
          onSpecDownload={() =>
            downloadDoc('DECISION', `Решение о выпуске №${issue?.id}.pdf`)
          }
          secondExtension={issueSigAvailable ? '.SIG' : undefined}
          onSecondSpecDownload={
            issueSigAvailable
              ? () =>
                downloadDoc(
                  'SIGNED_DECISION',
                  `Решение о выпуске №${issue?.id}.pdf.sig`
                )
              : undefined
          }
        />
      );

    const purchaseConsentAvailable =
      !['ISSUER_DOC_SIGNED', 'INITIAL', 'ISSUER_SIGNED'].includes(
        issue?.status
      ) ||
      (issue?.status === 'ISSUER_SIGNED' && !issue?.isIssuer);

    purchaseConsentAvailable &&
      docs.push(
        <LoadDocBtn
          title="Заявка на приобретение"
          extension=".PDF"
          onSpecDownload={() =>
            downloadDoc('PAYMENT_REQUIRED', 'Заявка на приобретение.pdf')
          }
          secondExtension={sigAvailable ? '.SIG' : undefined}
          onSecondSpecDownload={
            sigAvailable
              ? () =>
                downloadDoc(
                  'PAYMENT_SIGNED',
                  `Заявка на приобретение.pdf.sig`
                )
              : undefined
          }
        />
      );
    ['PAYMENT_DOCUMENT_UPLOADED', 'ISSUE_ACCEPT_SIGNED', 'COMPLETED'].includes(
      issue?.status
    ) &&
      issue?.issuePaymentDetails?.paymentType.code === 'FIAT' &&
      docs.push(
        <LoadDocBtn
          title="Документ подтверждающий оплату"
          onSpecDownload={() =>
            downloadDoc(
              'PAYMENT_CONFIRMATION',
              'Документ подтверждающий оплату.pdf'
            )
          }
        />
      );
    // ['PAYMENT_DOCUMENT_UPLOADED', 'ISSUE_ACCEPT_SIGNED', 'COMPLETED'].includes(issue?.status) && issue?.isIssuer &&
    // docs.push(
    //   <LoadDocBtn
    //     title="Признание выпуска состоявшимся"
    //     secondExtension={sigAvailable ? '.SIG' : ''}
    //     onSpecDownload={() =>
    //       downloadDoc(
    //         'CONFIRMATION',
    //         'Признание выпуска состоявшимся.pdf'
    //       )
    //     }
    //     onSecondSpecDownload={
    //       (['ISSUE_ACCEPT_SIGNED', 'COMPLETED'].includes(issue?.status))
    //         ? () =>
    //           downloadDoc(
    //             'CONFIRMATION_SIGNED',
    //             'Признание выпуска состоявшимся.pdf.sig'
    //           )
    //         : undefined
    //     }
    //   />
    // );
    // ['ISSUE_ACCEPT_SIGNED', 'COMPLETED'].includes(issue?.status) && !issue?.isIssuer &&
    // docs.push(
    //   <LoadDocBtn
    //     title="Признание выпуска состоявшимся"
    //     secondExtension={sigAvailable ? '.SIG' : ''}
    //     onSpecDownload={() =>
    //       downloadDoc(
    //         'CONFIRMATION',
    //         'Признание выпуска состоявшимся.pdf'
    //       )
    //     }
    //     onSecondSpecDownload={
    //       () =>
    //           downloadDoc(
    //             'CONFIRMATION_SIGNED',
    //             'Признание выпуска состоявшимся.pdf.sig'
    //           )
    //     }
    //   />
    // );
    return docs;
  }, [issue?.decision, issue?.status]);

  const [privateKey, setPrivateKey] = useState<string>('');

  const confirmSigning = () => {
    if (signMode === 'SIGN') {
      signIssueHandle();
    }
    if (signMode === 'APPROVE') {
      approveIssueHandle();
    }
  };

  const signIssueHandle = () => {
    try {
      new Wallet(privateKey);

      dispatch(
        signIssue({
          info: {
            decisionId: id,
            signedData: 'data',
            privateKey: privateKey,
          },
          callback: () => {
            setSigned(true);
          },
        })
      ).then((data) => {
        if (data.payload.transactionHash !== undefined) {
          setTransactionId(data.payload.transactionHash);
        }
      });
    } catch (e) {
      dispatch(
        setPush({
          message:
            'Некорректный формат ключа. Ожидаемый формат 0x... или 64 символа',
          type: 'error',
        })
      );
    }
  };

  const approveIssueHandle = () => {
    try {
      new Wallet(privateKey);
      dispatch(
        paymentConfirm({
          id,
          signedData: 'data',
          privateKey: privateKey,
        })
      ).then((data) => {
        if (data.payload.transactionHash !== undefined) {
          setSigned(true);
          setTransactionId(data.payload.transactionHash);
        }
      });
    } catch (e) {
      dispatch(
        setPush({
          message:
            'Некорректный формат ключа. Ожидаемый формат 0x... или 64 символа',
          type: 'error',
        })
      );
    }
  };

  console.log('issue?.status', issue?.status);

  const handleSign = () => {
    if (currentCert && issue?.decision.id) {
      dispatch(
        issue?.status === 'ISSUER_SIGNED'
          ? getUcpDoc({
            id: issue?.decision.id,
            type: 'PAYMENT_REQUIRED',
            contentType: 'application/pdf',
          })
          : getUcpDoc({
            id: issue?.decision.id,
            type:
              issue?.status === 'PAYMENT_DOCUMENT_UPLOADED'
                ? 'CONFIRMATION'
                : 'DECISION',
            contentType: 'application/pdf',
            preventDocConvertion: issue?.status === 'INITIAL',
          })
      ).then(async (res: any) => {
        console.log('doc loading result: ' + res);
        const { pdfSigned, signed } = await signEntity(res.payload.hash);
        if (pdfSigned && signed) {
          dispatch(
            signOperation({
              docType:
                issue?.status === 'ISSUER_SIGNED'
                  ? 'PAYMENT_SIGNED'
                  : issue?.status === 'PAYMENT_DOCUMENT_UPLOADED'
                    ? 'CONFIRMATION_SIGNED'
                    : 'SIGNED_DECISION',
              signature: pdfSigned,
              hash: signed,
              issuingDecisionId: issue?.decision.id,
            })
          ).then(() => {
            issue?.issuePaymentDetails?.paymentType?.code === 'FIAT'
              ? id && dispatch(getIssue({ id }))
              : issue?.status === 'COMPLETED'
                ? setSigned(true)
                : id && dispatch(getIssue({ id }));
          });
        }
      });
    }
  };

  const [file, setFile] = useState<File>();

  const handleApprove = () => {
    file &&
      dispatch(
        paymentUpload({
          id,
          file,
        })
      ).then(() => setSigned(true));
  };

  const detailsListLeft = useMemo(
    () => [
      [
        {
          title: 'Эмитент:',
          value: get(issue, 'issuer', '-'),
        },
        {
          title: 'ИНН:',
          value: get(issue, 'issuerTIN', '-'),
        },
      ],
      [
        {
          title: 'Получатель:',
          value: get(issue, 'recipient', '-'),
        },
        {
          title: 'ИНН:',
          value: get(issue, 'recipientTIN', '-'),
        },
      ],
      [
        {
          title: 'Дата создания:',
          value: getFormatDate(
            get(issue, 'initialDate', new Date()),
            'dd MMM yyyy',
            true
          ),
        },
        {
          title: 'Дата исполнения:',
          value: get(issue, 'finalizedDate')
            ? getFormatDate(
              get(issue, 'finalizedDate', new Date()),
              'dd MMM yyyy',
              true
            )
            : '-',
        },
      ],
    ],
    [issue]
  );

  return (
    <AppPageMain>
      <div className="container">
        <div className="row">
          <div className="col-19 col-lg-27 col-xl-35">
            <PageHeader
              items={[
                {
                  link: isHistory ? '/history' : '/assets',
                  title: isHistory ? 'Операции' : 'Активы',
                },
                { link: isHistory ? '/history' : '#', title: 'Выпуск ЦФА' },
              ]}
              iconType="assets"
              tabName={isHistory && 'issue'}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-19 col-lg-27 col-xl-35">
            {isSigned ? (
              <OperationComplete
                title="Выпуск цифровых финансовых активов"
                onClose={() => history.push('/history')}
                main={
                  <>
                    <p className="p p_font-size_32 p_font-weight_light mb_32 text-center">
                      {issue?.issuePaymentDetails?.paymentType?.code ===
                        'FIAT' ? (
                        issue?.isIssuer ? (
                          <>
                            Транзакция успешно отправлена
                            <br />и токены зачислены Приобретателю
                          </>
                        ) : (
                          'Подтверждение оплаты успешно отправлено!'
                        )
                      ) : issue?.isIssuer ? (
                        'Решение о выпуске ЦФА успешно подписано'
                      ) : (
                        'Транзакция встречного предоставления токенов подписана'
                      )}
                    </p>
                    <p
                      className={`p p_font-size_18 ${issue?.issuePaymentDetails?.paymentType?.code ===
                        'FIAT' && issue?.isIssuer
                        ? ''
                        : 'mb_32'
                        }`}
                    >
                      {issue?.issuePaymentDetails?.paymentType?.code === 'FIAT'
                        ? issue?.isIssuer
                          ? ''
                          : 'Ожидайте пока Продавец подтвердит получение денежных средств'
                        : issue?.isIssuer
                          ? 'Дождитесь подтверждения приобретателя'
                          : issue?.status === 'COMPLETED'
                            ? 'Вам зачислены токены эмитента'
                            : 'Ожидайте подтверждения выпуска Эмитентом'}
                    </p>

                    <>
                      {transactionId && (
                        <div className="flex align-center mb_32">
                          <p className="p p_font-size_18 mr_10">
                            ID транзакции:{' '}
                            {transactionId.slice(0, 15) +
                              '..' +
                              transactionId.slice(-10)}
                          </p>
                          <CopyIcon
                            className="cursor-pointer"
                            width={20}
                            height={20}
                            onClick={() => {
                              navigator.clipboard.writeText(transactionId);
                            }}
                          />
                        </div>
                      )}
                      <div className="flex">
                        {((issue?.isIssuer &&
                          issue?.issuePaymentDetails?.paymentType?.code ===
                          'FIAT') ||
                          issue?.issuePaymentDetails?.paymentType?.code !==
                          'FIAT') && (
                            <div className="mr_16">
                              <BlockchainExplorerButton trxnId={transactionId} />
                            </div>
                          )}
                        <IconBtn
                          title="Перейти в операции"
                          onClick={() => history.push('/history')}
                        />
                      </div>
                    </>
                  </>
                }
              />
            ) : (
              <DetailsContainer
                title={'Выпуск цифровых финансовых активов'}
                onClose={() => history.push(isHistory ? '/history' : '/assets')}
                skipFooterCheck={!isSignActive}
                infoTitle={{
                  steps: [
                    {
                      title: 'Параметры'
                    },
                    {
                      title: 'Подпись',
                      active: issue?.status === 'INITIAL',
                    },
                    {
                      title: 'Согласование',
                      active: issue?.status === 'ISSUER_SIGNED',
                    },
                    {
                      title: 'Выпуск',
                      active:
                        issue?.status === 'RECIPIENT_SIGNED' ||
                        issue?.status === 'PAYMENT_DOCUMENT_UPLOADED' ||
                        issue?.status === 'ISSUE_ACCEPT_SIGNED' ||
                        issue?.status === 'RECIPIENT_DOC_SIGNED',
                    },
                  ],
                  // title: 'Операция:',
                  title: ' ',
                  description: `Выпуск цифровых финансовых активов
                на сумму ${currencyFormat(
                    Number(get(issue, 'initialValue', 0))
                  )}`,
                  status: getIssueBadge(
                    get(issue, 'status', ''),
                    get(issue, 'isIssuer', false)
                  ),
                }}
                leftContent={{
                  title: '',
                  docTitle: 'Документы:',
                  detailsList: detailsListLeft,
                  docs: decisionDocs,
                }}
                transactionDetails={{
                  fromName: get(issue, 'issuer', ''),
                  fromHash: get(issue, 'issuerBlockchainAddress', ''),
                  toName: get(issue, 'recipient', ''),
                  toHash: get(issue, 'recipientBlockchainAddress', ''),
                  transactionType: 'issue',
                }}
                rightTopComponent={
                  <div className="assets-details__col assets-details__col-12 cfa-release-right">
                    {issue?.issuePaymentDetails?.paymentType?.code === 'FIAT' &&
                      issue?.isInitiator && (
                        <IconBtn
                          outlined
                          sm
                          title="Уточнить реквизиты"
                          onClick={() =>
                            history.push('/profile/company', {
                              tabName: 'bankInfo',
                            })
                          }
                        />
                      )}
                    <h2 className="text-2 assets-details__title">
                      Параметры операции:
                    </h2>
                    <div className="assets-details__item">
                      <LabelValueItem
                        title={
                          issue?.status === 'COMPLETED'
                            ? 'Количество выпущенных токенов:'
                            : 'Количество выпускаемых токенов:'
                        }
                        text={issue?.tokenCount}
                      />
                    </div>
                    <div className="assets-details__item">
                      <LabelValueItem
                        title="Денежное требование по токену"
                        text={currencyFormat(issue?.tokenPrice || 100)}
                      />
                    </div>
                    <div className="assets-details__item">
                      <LabelValueItem
                        title="Общий объем денежных требований"
                        text={currencyFormat(
                          issue?.tokenPrice * issue?.tokenCount
                        )}
                      />
                    </div>
                    <div className="assets-details__item">
                      <LabelValueItem
                        title="Срок исполнения требований"
                        text={getFormatDate(
                          issue?.expiryDate || new Date(Date.now()),
                          'dd.MM.yyyy',
                          true
                        )}
                      />
                    </div>
                    {issue?.issuePaymentDetails?.paymentType?.code ===
                      'FIAT' && (
                        <>
                          <div className="assets-details__item">
                            <LabelValueItem
                              title="Сумма для оплаты"
                              text={currencyFormat(issue?.initialValue || 0)}
                            />
                          </div>
                        </>
                      )}
                    {issue && issue?.blockchainTransactionHash !== null && (
                      <>
                        <p className="p p_font-size_12 p_color_light-grey">
                          ID транзакции
                        </p>
                        <div className="mb_32">
                          <BlockchainTransactionInfo
                            hash={issue.blockchainTransactionHash}
                          />
                        </div>
                      </>
                    )}
                    {issue?.issuePaymentDetails?.paymentType?.code ===
                      'FIAT' ? (
                      <>
                        <h2 className="text-2 assets-details__title">
                          Реквизиты платежа
                        </h2>
                        <div className="assets-details__item">
                          <LabelValueItem
                            title=""
                            text={
                              <div>
                                {issue?.issuer}
                                <br />
                                <br />
                                ИНН: {issue?.issuerTIN || '-'}
                                <br />
                                КПП: {issue?.issuePaymentDetails.kpp || '-'}
                                <br />
                                ОГРН:{' '}
                                {issue?.issuePaymentDetails.regNumber || '-'}
                                <br />
                                Адрес:{' '}
                                {issue?.issuePaymentDetails.address || '-'}
                                <br />
                                <br />
                                Р/С:{' '}
                                {issue?.issuePaymentDetails.bankAccountInfo
                                  ?.accountNumber || '-'}
                                <br />
                                Банк:{' '}
                                {issue?.issuePaymentDetails.bankAccountInfo
                                  ?.bankName || '-'}
                                <br />
                                БИК:{' '}
                                {issue?.issuePaymentDetails.bankAccountInfo
                                  ?.bik || '-'}
                                <br />
                                К/С:{' '}
                                {issue?.issuePaymentDetails.bankAccountInfo
                                  ?.corrAccountNumber || '-'}
                              </div>
                            }
                          />
                        </div>

                        <div className="assets-details__item">
                          <LabelValueItem
                            title="Основание платежа:"
                            text={`Оплата выпуска ЦФА "${issue?.tokenName
                              }" по решению №${issue?.id} от ${getFormatDate(
                                issue?.initialDate,
                                'dd.MM.yyyy',
                                true
                              )}г. Без НДС`}
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        <h2 className="text-2 assets-details__title">
                          Встречное предоставление:
                        </h2>
                        <div className="assets-details__item">
                          <p className="p p_font-size_12 p_color_light-grey">
                            Наименование токенов, передаваемых за выпуск
                          </p>
                          <a href="/#">
                            {issue?.issuePaymentDetails?.paymentType?.name}
                          </a>
                        </div>
                        <div className="assets-details__item">
                          <LabelValueItem
                            title="Количество токенов, передаваемых за выпуск"
                            text={
                              issue?.issuePaymentDetails?.paymentTokenCount || 0
                            }
                          />
                        </div>
                      </>
                    )}
                  </div>
                }
                footer={
                  !issue?.isIssuer ? (
                    ['ISSUER_SIGNED', 'RECIPIENT_SIGNED', 'RECIPIENT_DOC_SIGNED'].includes(issue?.status) ? (
                      <>
                        {issue?.status === 'ISSUER_SIGNED' && (
                          <div className="flex issuer-footer align-center">
                            <div className="footer__go-back">
                              <AppCheckbox
                                onChangeValue={(val) => {
                                  setApproveRisk(val);
                                }}
                                titleComponent={
                                  <div className="module-title-text__desc">
                                    Подтверждаю, что ознакомлен с{' '}
                                    <a
                                      className="a__link"
                                      href="https://account.cfa.digital/risk_declaration.pdf"
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      декларацией о рисках
                                    </a>
                                  </div>
                                }
                              />
                            </div>
                            <div className="flex justify-end issuer-footer align-center">
                              <div className="flex justify-end">
                                <UiSelect
                                  label="Выберите подпись"
                                  options={certificatesList}
                                  lg
                                  value={selectedCertificate}
                                  onChange={onUserSelectCertificate}
                                />
                                <IconBtn
                                  onClick={handleSign}
                                  lg
                                  disabled={!currentCert || !approveRisk}
                                  icon={<SignIcon />}
                                  title="Подписать согласие"
                                />
                              </div>
                            </div>
                          </div>
                        )}
                        {issue?.status === 'RECIPIENT_SIGNED' && (
                          <div className="flex justify-end issuer-footer align-center">
                            <GoBack onClick={() => history.push('/exchange')} />
                            <div className="flex justify-end">
                              <div className="upload">
                                <LoadDocBtn
                                  title={
                                    file
                                      ? file.name
                                      : 'Документ подтверждающий оплату'
                                  }
                                  extension="Выберите файл"
                                  sm={false}
                                />
                                <input
                                  id="file"
                                  type="file"
                                  onChange={(e: any) =>
                                    setFile(e.target.files[0])
                                  }
                                />
                              </div>
                              <IconBtn
                                onClick={handleApprove}
                                lg
                                disabled={!file}
                                icon={<SignIcon />}
                                title="Подтвердить оплату"
                              />
                            </div>
                          </div>
                        )}
                        {issue?.status === 'RECIPIENT_DOC_SIGNED' && (
                          <div className="flex justify-end issuer-footer align-center">
                            <GoBack onClick={() => history.push('/cfa/sign')} />
                            <div className="flex justify-end">
                              <div className="mr_10 flex align-stretch">
                                <PrivateKeyButton
                                  onKeyChange={(key) => {
                                    setSignMode('SIGN');
                                    setPrivateKey(key || '');
                                  }}
                                />
                              </div>
                              <IconBtn
                                onClick={confirmSigning}
                                lg
                                disabled={!privateKey}
                                icon={<SignIcon />}
                                title="Подтвердить оплату"
                              />
                            </div>
                          </div>
                        )}
                      </>
                    ) : null
                  ) : issue?.status === 'ISSUER_SIGNED' ? null : (
                    ['ISSUER_DOC_SIGNED', 'INITIAL', 'PAYMENT_DOCUMENT_UPLOADED', 'ISSUE_ACCEPT_SIGNED'].includes(issue?.status) ? (
                      <>
                        {issue?.status === 'ISSUER_DOC_SIGNED' && (
                          <div className="flex justify-end issuer-footer align-center">
                            <GoBack onClick={() => history.push('/cfa/sign')} />
                            <div className="flex justify-end">
                              <div className="mr_10 flex align-stretch">
                                <PrivateKeyButton
                                  onKeyChange={(key) => {
                                    setPrivateKey(key || '');
                                  }}
                                />
                              </div>
                              <IconBtn
                                onClick={signIssueHandle}
                                lg
                                disabled={!privateKey}
                                icon={<SignIcon />}
                                title="Подписать транзакцию Ключом"
                              />
                            </div>
                          </div>
                        )}
                        {issue?.status === 'INITIAL' && (
                          <div className="flex issuer-footer align-center">
                            <div className="footer__go-back"></div>
                            <div className="flex justify-end issuer-footer align-center">
                              <div className="flex justify-end">
                                <UiSelect
                                  label="Выберите подпись"
                                  options={certificatesList}
                                  lg
                                  value={selectedCertificate}
                                  onChange={onUserSelectCertificate}
                                />
                                <IconBtn
                                  onClick={handleSign}
                                  lg
                                  disabled={!currentCert}
                                  icon={<SignIcon />}
                                  title="Подписать решение"
                                />
                              </div>
                            </div>
                          </div>
                        )}
                        {issue?.status === 'PAYMENT_DOCUMENT_UPLOADED' && (
                          <div className="flex issuer-footer align-center">
                            <div className="footer__go-back">
                              <AppCheckbox
                                onChangeValue={(val) => {
                                  setApprovePayment(val);
                                }}
                                titleComponent={
                                  <div className="module-title-text__desc">
                                    Подтверждаю, что оплата выпуска произведена
                                  </div>
                                }
                              />
                            </div>
                            <div className="flex justify-end issuer-footer align-center">
                              <div className="flex justify-end">
                                <UiSelect
                                  label="Выберите подпись"
                                  options={certificatesList}
                                  lg
                                  value={selectedCertificate}
                                  onChange={onUserSelectCertificate}
                                />
                                <IconBtn
                                  onClick={handleSign}
                                  lg
                                  disabled={!currentCert || !approvePayment}
                                  icon={<SignIcon />}
                                  title="Подписать признание выпуска"
                                />
                              </div>
                            </div>
                          </div>
                        )}
                        {issue?.status === 'ISSUE_ACCEPT_SIGNED' && (
                          <div className="flex justify-end issuer-footer align-center">
                            <GoBack onClick={() => history.push('/exchange')} />
                            <div className="flex justify-end">
                              <div className="mr_10 flex align-stretch">
                                <PrivateKeyButton
                                  onKeyChange={(key) => {
                                    setSignMode('APPROVE');
                                    setPrivateKey(key || '');
                                  }}
                                />
                              </div>
                              <IconBtn
                                onClick={confirmSigning}
                                lg
                                disabled={!privateKey}
                                icon={<SignIcon />}
                                title="Зачислить токены приобретателю"
                              />
                            </div>
                          </div>
                        )}
                      </>
                    ) : null
                  )
                }
              />
            )}
          </div>
        </div>
      </div>
    </AppPageMain>
  );
};

export default CfaInfoPage;
